import { pgTable, text, boolean, timestamp, integer, index, uniqueIndex, foreignKey, jsonb } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import {
  chatRoomType,
  chatRoomKind,
  chatParticipantRole,
  messageDeliveryStatus,
  chatParticipantStatus
} from './enums';
import { user } from './user';
import { adminUser } from './admin';
import { file } from './files';

// ==========================================
// CHAT ROOM - Комната чата (DM, Support, Game)
// ==========================================
export const chatRoom = pgTable('ChatRoom', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Тип чата (legacy: DIRECT/SUPPORT/GAME_LOBBY)
  type: chatRoomType().notNull(),

  // Политика-уровневая категория чата (E2EE / service / admin / etc).
  // В отличие от `type`, `kind` определяет применяется ли сквозное
  // шифрование. По умолчанию DIRECT — обычный незашифрованный чат.
  kind: chatRoomKind().default('DIRECT').notNull(),

  // Включено ли сквозное шифрование. ВАЖНО: поле IMMUTABLE после create —
  // сервис-слой обязан запрещать UPDATE этого поля. В БД это инвариант
  // не enforced (триггеров в проекте избегают), enforcement в service layer.
  // Для E2EE-комнат: chatMessage.content пустой, реальный ciphertext в
  // EncryptedMessage. См. packages/db/src/drizzle/schema/e2ee.ts.
  isE2EE: boolean().default(false).notNull(),

  // Момент включения E2EE для комнаты. Заполняется при create вместе с
  // `isE2EE=true` (сейчас — только для новых DIRECT, если оба участника
  // прошли PIN-онбординг). Null для plaintext-комнат. Используется UI для
  // отрисовки divider "С этого момента сообщения зашифрованы" между
  // исторической plaintext-частью и зашифрованным хвостом (актуально,
  // если в будущем будет переход plaintext→E2EE на существующей комнате).
  e2eeEnabledAt: timestamp({ precision: 3, mode: 'date' }),

  // Название (для game lobby) - null для DM
  name: text(),

  // Ссылка на внешнюю сущность (например, gameLobbyId)
  externalEntityType: text(), // 'GAME_LOBBY', 'SUPPORT_TICKET' и т.д.
  externalEntityId: integer(),

  // Метаданные
  metadata: jsonb(),

  // Флаги
  isActive: boolean().default(true).notNull(),

  // Закреплённое сообщение. FK объявлен через relations (circular ref между chatRoom↔chatMessage).
  pinnedMessageId: integer(),

  // Timestamps
  lastMessageAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ChatRoom_type_idx').using('btree', table.type.asc().nullsLast()),
  index('ChatRoom_lastMessageAt_idx').using('btree', table.lastMessageAt.desc().nullsLast()),
  index('ChatRoom_externalEntity_idx').using('btree',
    table.externalEntityType.asc().nullsLast(),
    table.externalEntityId.asc().nullsLast()
  ),
  index('ChatRoom_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  // FK to chatMessage defined post-declaration to avoid circular reference TS7022
]);

// ==========================================
// CHAT PARTICIPANT - Участник чата
// ==========================================
export const chatParticipant = pgTable('ChatParticipant', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  chatRoomId: integer().notNull(),

  // Полиморфная связь: user ИЛИ admin
  userId: integer(),      // null если это admin
  adminUserId: integer(), // null если это user

  // Роль в чате
  role: chatParticipantRole().default('MEMBER').notNull(),

  // Название отдела (для SUPPORT чатов, где админ пишет от имени отдела)
  departmentName: text('department_name'), // "Поддержка" | "Забота о клиентах" | null

  // Статус
  status: chatParticipantStatus().default('ACTIVE').notNull(),

  // Настройки участника
  isMuted: boolean().default(false).notNull(),
  mutedUntil: timestamp({ precision: 3, mode: 'date' }),

  // Бан в чате (устанавливается модератором)
  isBanned: boolean().default(false).notNull(),
  bannedAt: timestamp({ precision: 3, mode: 'date' }),
  bannedByAdminId: integer(),
  banReason: text(),

  // Прочитанные сообщения (для быстрого подсчёта непрочитанных)
  lastReadMessageId: integer(),
  lastReadAt: timestamp({ precision: 3, mode: 'date' }),

  // Timestamps
  joinedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  leftAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  // Уникальность: один user/admin может быть в чате только один раз
  uniqueIndex('ChatParticipant_chatRoom_user_key').using('btree',
    table.chatRoomId.asc().nullsLast(),
    table.userId.asc().nullsLast()
  ),
  uniqueIndex('ChatParticipant_chatRoom_admin_key').using('btree',
    table.chatRoomId.asc().nullsLast(),
    table.adminUserId.asc().nullsLast()
  ),
  index('ChatParticipant_chatRoomId_idx').using('btree', table.chatRoomId.asc().nullsLast()),
  index('ChatParticipant_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('ChatParticipant_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
  index('ChatParticipant_status_idx').using('btree', table.status.asc().nullsLast()),
  foreignKey({
    columns: [table.chatRoomId],
    foreignColumns: [chatRoom.id],
    name: 'ChatParticipant_chatRoomId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'ChatParticipant_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'ChatParticipant_adminUserId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// CHAT MESSAGE - Сообщение
// ==========================================
export const chatMessage = pgTable('ChatMessage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  chatRoomId: integer().notNull(),

  // Автор (полиморфно: user или admin)
  senderUserId: integer(),
  senderAdminId: integer(),

  // Контент (текст + эмодзи)
  content: text().notNull(),

  // Тип сообщения (для системных)
  isSystemMessage: boolean().default(false).notNull(),

  // Reply (цитирование)
  replyToMessageId: integer(),

  // Статус доставки
  status: messageDeliveryStatus().default('SENT').notNull(),

  // Редактирование
  isEdited: boolean().default(false).notNull(),
  editedAt: timestamp({ precision: 3, mode: 'date' }),

  // Soft delete
  isDeleted: boolean().default(false).notNull(),
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  deletedBy: integer(), // userId или adminId (понимаем по контексту)

  // Закрепление сообщения
  isPinned: boolean().default(false).notNull(),
  pinnedAt: timestamp({ precision: 3, mode: 'date' }),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ChatMessage_chatRoomId_idx').using('btree', table.chatRoomId.asc().nullsLast()),
  // Составной индекс для cursor-based пагинации
  index('ChatMessage_chatRoomId_createdAt_idx').using('btree',
    table.chatRoomId.asc().nullsLast(),
    table.createdAt.desc().nullsLast()
  ),
  index('ChatMessage_senderUserId_idx').using('btree', table.senderUserId.asc().nullsLast()),
  index('ChatMessage_senderAdminId_idx').using('btree', table.senderAdminId.asc().nullsLast()),
  index('ChatMessage_replyToMessageId_idx').using('btree', table.replyToMessageId.asc().nullsLast()),
  index('ChatMessage_isDeleted_idx').using('btree', table.isDeleted.asc().nullsLast()),
  foreignKey({
    columns: [table.chatRoomId],
    foreignColumns: [chatRoom.id],
    name: 'ChatMessage_chatRoomId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.senderUserId],
    foreignColumns: [user.id],
    name: 'ChatMessage_senderUserId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.senderAdminId],
    foreignColumns: [adminUser.id],
    name: 'ChatMessage_senderAdminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// Self-reference для replyToMessageId добавляется отдельно в relations

// ==========================================
// MESSAGE REACTION - Реакции на сообщения
// ==========================================
export const messageReaction = pgTable('MessageReaction', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  messageId: integer().notNull(),

  // Автор реакции (полиморфно)
  userId: integer(),
  adminUserId: integer(),

  // Emoji реакции (UTF-8)
  emoji: text().notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  // Один пользователь - одна конкретная реакция на сообщение
  uniqueIndex('MessageReaction_message_user_emoji_key').using('btree',
    table.messageId.asc().nullsLast(),
    table.userId.asc().nullsLast(),
    table.emoji.asc().nullsLast()
  ),
  uniqueIndex('MessageReaction_message_admin_emoji_key').using('btree',
    table.messageId.asc().nullsLast(),
    table.adminUserId.asc().nullsLast(),
    table.emoji.asc().nullsLast()
  ),
  index('MessageReaction_messageId_idx').using('btree', table.messageId.asc().nullsLast()),
  index('MessageReaction_emoji_idx').using('btree', table.emoji.asc().nullsLast()),
  foreignKey({
    columns: [table.messageId],
    foreignColumns: [chatMessage.id],
    name: 'MessageReaction_messageId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'MessageReaction_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'MessageReaction_adminUserId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// MESSAGE READ RECEIPT - Прочтения сообщений (детальные)
// ==========================================
export const messageReadReceipt = pgTable('MessageReadReceipt', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  messageId: integer().notNull(),

  // Кто прочитал (полиморфно)
  userId: integer(),
  adminUserId: integer(),

  readAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('MessageReadReceipt_message_user_key').using('btree',
    table.messageId.asc().nullsLast(),
    table.userId.asc().nullsLast()
  ),
  uniqueIndex('MessageReadReceipt_message_admin_key').using('btree',
    table.messageId.asc().nullsLast(),
    table.adminUserId.asc().nullsLast()
  ),
  index('MessageReadReceipt_messageId_idx').using('btree', table.messageId.asc().nullsLast()),
  foreignKey({
    columns: [table.messageId],
    foreignColumns: [chatMessage.id],
    name: 'MessageReadReceipt_messageId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'MessageReadReceipt_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'MessageReadReceipt_adminUserId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// USER BLOCK - Блокировки пользователей (для DM)
// ==========================================
export const userBlock = pgTable('UserBlock', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Кто заблокировал
  blockerId: integer().notNull(),

  // Кого заблокировал
  blockedId: integer().notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('UserBlock_blocker_blocked_key').using('btree',
    table.blockerId.asc().nullsLast(),
    table.blockedId.asc().nullsLast()
  ),
  index('UserBlock_blockerId_idx').using('btree', table.blockerId.asc().nullsLast()),
  index('UserBlock_blockedId_idx').using('btree', table.blockedId.asc().nullsLast()),
  foreignKey({
    columns: [table.blockerId],
    foreignColumns: [user.id],
    name: 'UserBlock_blockerId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.blockedId],
    foreignColumns: [user.id],
    name: 'UserBlock_blockedId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// CHAT MESSAGE ATTACHMENT - Вложения к сообщениям
// ==========================================
export const chatMessageAttachment = pgTable('ChatMessageAttachment', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  chatMessageId: integer().notNull(),
  fileId: integer().notNull(),

  // Тип вложения: 'image', 'video', 'audio', 'document'
  type: text().notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('ChatMessageAttachment_chatMessageId_idx').using('btree', table.chatMessageId.asc().nullsLast()),
  index('ChatMessageAttachment_fileId_idx').using('btree', table.fileId.asc().nullsLast()),
  foreignKey({
    columns: [table.chatMessageId],
    foreignColumns: [chatMessage.id],
    name: 'ChatMessageAttachment_chatMessageId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.fileId],
    foreignColumns: [file.id],
    name: 'ChatMessageAttachment_fileId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// USER VIOLATION - Нарушения пользователей (для модерации)
// ==========================================
export const userViolation = pgTable('UserViolation', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Кто нарушил
  userId: integer().notNull(),

  // Тип нарушения: 'WARNING' | 'MUTE' | 'KICK' | 'BAN' | 'MESSAGE_DELETED'
  violationType: text().notNull(),

  // Контекст нарушения: 'CHAT' | 'GAME' | 'GLOBAL'
  context: text().notNull(),
  contextEntityId: integer(), // chatRoomId или lobbyId (опционально)
  relatedMessageId: integer(), // если связано с конкретным сообщением

  // Детали
  reason: text().notNull(),
  duration: integer(), // в минутах (для mute), null = бессрочно
  expiresAt: timestamp({ precision: 3, mode: 'date' }), // когда истекает наказание

  // Кто выдал наказание
  adminId: integer().notNull(),

  // Статус наказания
  isActive: boolean().default(true).notNull(),
  revokedAt: timestamp({ precision: 3, mode: 'date' }),
  revokedByAdminId: integer(),
  revokeReason: text(),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('UserViolation_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('UserViolation_violationType_idx').using('btree', table.violationType.asc().nullsLast()),
  index('UserViolation_context_idx').using('btree',
    table.context.asc().nullsLast(),
    table.contextEntityId.asc().nullsLast()
  ),
  index('UserViolation_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('UserViolation_expiresAt_idx').using('btree', table.expiresAt.asc().nullsLast()),
  index('UserViolation_adminId_idx').using('btree', table.adminId.asc().nullsLast()),
  index('UserViolation_createdAt_idx').using('btree', table.createdAt.desc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserViolation_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminId],
    foreignColumns: [adminUser.id],
    name: 'UserViolation_adminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.revokedByAdminId],
    foreignColumns: [adminUser.id],
    name: 'UserViolation_revokedByAdminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.relatedMessageId],
    foreignColumns: [chatMessage.id],
    name: 'UserViolation_relatedMessageId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);
